// Normalize LLM markdown output so every paragraph / heading / list / table has
// a proper blank-line separator. Handles common LLM failure modes:
//   1) Single-newline output (paragraphs glued together)
//   2) "Bold: rest" style key:value lines without dash prefix
//   3) Plain-text "titles" without ## prefix
//
// Mirrors backend `normalize_markdown` so old DB-stored messages render correctly too.

const LIST_RE = /^\s*([-*+]\s+|\d+[.)]\s+)/;
const HEADING_RE = /^#{1,6}\s+\S/;
const HR_RE = /^(-{3,}|\*{3,}|_{3,})\s*$/;
// "**Bold**: text" / "**Bold** — text" / "Title Cased Words: text" (followed by content)
const KV_RE = /^\s*(\*\*[^*]+\*\*\s*[:：—–\-]|[A-Z][^:\n—]{2,40}\s*[:：—–])\s+\S/;

export function normalizeMarkdown(text) {
  if (!text || typeof text !== "string") return text || "";

  const rawLines = text.replace(/\r\n/g, "\n").split("\n").map((l) => l.replace(/\s+$/, ""));
  const out = [];

  for (let i = 0; i < rawLines.length; i++) {
    const ln = rawLines[i];
    const prev = out.length ? out[out.length - 1] : "";

    // Heading: blank line before + after
    if (HEADING_RE.test(ln)) {
      if (prev.trim() !== "") out.push("");
      out.push(ln);
      if (i + 1 < rawLines.length && rawLines[i + 1].trim() !== "") out.push("");
      continue;
    }

    // Horizontal rule
    if (HR_RE.test(ln)) {
      if (prev.trim() !== "") out.push("");
      out.push(ln);
      continue;
    }

    // Table row
    const isTable = ln.trimStart().startsWith("|");
    if (isTable && !prev.trimStart().startsWith("|") && prev.trim() !== "") {
      out.push("");
    }

    // List item: blank line before first item
    const isList = LIST_RE.test(ln);
    const prevIsList = LIST_RE.test(prev);
    if (isList && prev.trim() !== "" && !prevIsList) {
      out.push("");
    }

    out.push(ln);

    // List ended — add blank line before next non-list non-empty line
    if (isList && i + 1 < rawLines.length) {
      const nxt = rawLines[i + 1];
      const nxtIsList = LIST_RE.test(nxt);
      if (nxt.trim() !== "" && !nxtIsList && !nxt.trimStart().startsWith("|")) {
        out.push("");
      }
    }
  }

  // Collapse 3+ blank lines
  let cleaned = out.join("\n").replace(/\n{3,}/g, "\n\n").trim();

  // Convert runs of 3+ "Bold: rest" lines into bullet lists
  const lines = cleaned.split("\n");
  const final = [];
  let i2 = 0;
  while (i2 < lines.length) {
    if (KV_RE.test(lines[i2])) {
      let j = i2;
      while (j < lines.length && KV_RE.test(lines[j])) j++;
      const runLen = j - i2;
      if (runLen >= 3) {
        // ensure blank line before
        if (final.length && final[final.length - 1].trim() !== "") final.push("");
        for (let k = i2; k < j; k++) {
          const l = lines[k];
          final.push(l.trimStart().startsWith("- ") ? l : "- " + l.trimStart());
        }
        // blank line after if more content follows
        if (j < lines.length && lines[j].trim() !== "") final.push("");
        i2 = j;
        continue;
      }
    }
    final.push(lines[i2]);
    i2++;
  }
  cleaned = final.join("\n").replace(/\n{3,}/g, "\n\n").trim();

  // Aggressive paragraph separation: any two consecutive non-structural lines → insert blank line
  const isStructural = (s) =>
    LIST_RE.test(s) ||
    s.trimStart().startsWith("|") ||
    HEADING_RE.test(s) ||
    HR_RE.test(s);
  const pLines = cleaned.split("\n");
  const out2 = [];
  for (let idx = 0; idx < pLines.length; idx++) {
    out2.push(pLines[idx]);
    if (idx + 1 >= pLines.length) continue;
    const nxt = pLines[idx + 1];
    if (pLines[idx].trim() === "" || nxt.trim() === "") continue;
    if (isStructural(pLines[idx]) || isStructural(nxt)) continue;
    out2.push("");
  }
  cleaned = out2.join("\n").replace(/\n{3,}/g, "\n\n").trim();

  // Fallback: if no structural markdown markers anywhere, treat every line as paragraph
  const hasStructural = /(^|\n)\s*(#{1,6}\s|[-*+]\s|\d+[.)]\s|\|)/.test(cleaned);
  if (!hasStructural && cleaned.includes("\n")) {
    cleaned = cleaned
      .split("\n")
      .map((p) => p.trim())
      .filter((p) => p.length > 0)
      .join("\n\n");
  }

  return cleaned;
}
